import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBasket } from '@/lib/cart';
import { useAuth } from '@/hooks/useAuth';
import { trpc } from '@/lib/trpc';

export default function BasketPage() {
  const { items, removeItem, clearBasket, getItemCount, getTotalPrice } = useBasket();
  const { user } = useAuth();
  const nav = useNavigate();

  const itemCount  = getItemCount();
  const totalPrice = getTotalPrice();
  const hasFree    = items.some(i => i.price === 0);

  const [error, setError] = useState<string | null>(null);

  const initiatePayment = trpc.purchases.initiatePayment.useMutation({
    onSuccess: (res) => {
      // Empty the basket and forward to the payment gateway
      clearBasket();
      window.location.href = res.paymentUrl;
    },
    onError: (e) => setError(e.message),
  });

  const handleCheckout = () => {
    setError(null);
    if (!user) {
      // Should be unreachable because of ProtectedRoute, but be defensive
      nav('/login');
      return;
    }
    initiatePayment.mutate({
      courseIds: items.map(i => i.id),
    });
  };

  /* ── Empty ── */
  if (items.length === 0) return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6">
      <div className="text-center max-w-xs">
        <div className="w-24 h-24 mp-card rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-12 h-12 text-[#3F3F46]" fill="none" viewBox="0 0 24 24"
               stroke="currentColor" strokeWidth={1.4}>
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0
                 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114
                 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0
                 110-1.5.75.75 0 010 1.5zm12.75 0a.75.75 0 110-1.5.75.75 0 010 1.5z" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-white mb-3">Your basket is empty</h1>
        <p className="text-[#E4E4E7] text-lg mb-8">
          Add courses from the catalogue to get started.
        </p>
        <Link to="/courses"
              className="mp-btn mp-btn-primary px-7 py-3 text-base inline-flex">
          Browse Courses
        </Link>
      </div>
    </div>
  );

  /* ── Main ── */
  return (
    <div className="min-h-screen bg-black py-16 px-4 md:px-6">
      <div className="max-w-2xl mx-auto">

        {/* Breadcrumb + heading */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4 text-sm text-[#52525B]">
            <Link to="/" className="hover:text-[#A1A1AA] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-[#E4E4E7]">Basket</span>
          </div>
          <h1 className="text-4xl font-bold text-white tracking-tight">Your Basket</h1>
          <p className="text-[#71717A] text-lg mt-1">
            {itemCount} item{itemCount !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_280px] items-start">

          {/* Item list */}
          <div>
            <ul className="space-y-3 mb-4">
              {items.map(item => (
                <li key={item.id}
                    className="mp-card p-4 flex items-center gap-4 group
                               hover:border-[#3F3F46] transition-colors
                               shadow-sm">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br
                                  from-yellow-400/20 to-yellow-400/5
                                  flex items-center justify-center text-yellow-400
                                  font-bold text-lg shrink-0">
                    {item.title[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#E4E4E7] font-semibold text-sm leading-snug truncate">
                      {item.title}
                    </p>
                    <p className="text-yellow-400 font-bold text-base mt-0.5">
                      {item.price === 0 ? 'Free' : `${item.price} KD`}
                    </p>
                  </div>
                  <button
                    onClick={() => removeItem(item.id)}
                    aria-label={`Remove ${item.title}`}
                    className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center
                               text-[#3F3F46] hover:text-red-400 hover:bg-red-400/10
                               opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24"
                         stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0
                           01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1
                           1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
            <button onClick={clearBasket}
                    className="text-[#52525B] hover:text-red-400 text-sm transition-colors
                               flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24"
                   stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5
                     4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Clear basket
            </button>
          </div>

          {/* Summary */}
          <div className="mp-card p-6 sticky top-24 shadow-sm">
            <h2 className="text-lg font-bold text-white mb-5">Order Summary</h2>
            <div className="space-y-3 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-[#71717A]">{itemCount} course{itemCount !== 1 ? 's' : ''}</span>
                <span className="text-[#E4E4E7] font-semibold">{totalPrice} KD</span>
              </div>
              {hasFree && (
                <div className="flex justify-between text-sm">
                  <span className="text-[#71717A]">Free courses</span>
                  <span className="text-green-400 font-semibold">included</span>
                </div>
              )}
            </div>
            <div className="mp-divider my-4" />
            <div className="flex justify-between items-center mb-6">
              <span className="text-white font-bold">Total</span>
              <span className="text-yellow-400 font-bold text-2xl">{totalPrice} KD</span>
            </div>

            {error && (
              <p className="text-red-400 text-sm mb-3">{error}</p>
            )}

            <button
              onClick={handleCheckout}
              disabled={initiatePayment.isPending}
              className="mp-btn mp-btn-primary w-full py-4 rounded-2xl text-base
                         hover:scale-[1.01] transition-transform disabled:opacity-50"
            >
              {initiatePayment.isPending ? (
                <>
                  <span className="w-4 h-4 border-2 border-black border-t-transparent
                                   rounded-full animate-spin mr-2" />
                  Opening payment…
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24"
                       stroke="currentColor" strokeWidth={2.2}>
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  Pay {totalPrice} KD
                </>
              )}
            </button>

            <p className="text-[#52525B] text-[11px] text-center mt-2">
              Access to purchased courses is for 1 year from purchase.
            </p>

            <div className="flex items-center justify-center gap-1.5 mt-4">
              <svg className="w-3.5 h-3.5 text-[#52525B]" fill="none" viewBox="0 0 24 24"
                   stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="text-[#52525B] text-xs">Secured payment — KNET, Visa, Mastercard</span>
            </div>
            <Link to="/courses"
                  className="block text-center text-[#52525B] hover:text-[#A1A1AA]
                             text-xs mt-4 transition-colors">
              ← Continue shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
